
const getStoredData = (key) => {
  try {
    const val = localStorage.getItem(key);
    return val ? JSON.parse(val) : null;
  } catch { return null; }
};

const setStoredData = (key, val) => {
  try { localStorage.setItem(key, JSON.stringify(val)); } catch { }
};

const removeStoredData = (key) => {
  try { localStorage.removeItem(key); } catch { }
};

// Storage keys
export const STORAGE_KEYS = {
  USER_DATA: 'rabs_user_data',
  TOKENS: 'rabs_tokens',
  PERMISSIONS: 'rabs_permissions',
  CRM_SETTINGS: 'rabs_crm_settings',
  FCM_TOKEN: 'rabs_fcm_token',
};

// Read runtime client config injected via /public/config.js
const getClientConfig = () => (typeof window !== 'undefined' && window.APP_CONFIG) || {};

// Always ensure trailing slash
const getBase = () => {
  const cfg = getClientConfig();
  if (!cfg.apiBaseUrl) return null;
  return cfg.apiBaseUrl.endsWith('/') ? cfg.apiBaseUrl : cfg.apiBaseUrl + '/';
};

// ─── Build absolute URL for server-hosted files (uploads, etc.) ──
export const getFileUrl = (path) => {
  if (!path) return '';
  if (/^https?:\/\//i.test(path)) return path;
  const cfg = getClientConfig();
  if (!cfg.apiBaseUrl) return path;
  const base = cfg.apiBaseUrl.replace(/\/api\/?$/, '');
  const clean = path.startsWith('/') ? path : '/' + path;
  return base + clean;
};

// ─── Login ───────────────────────────────────────────────
export const loginApi = async (username, password) => {
  const cfg = getClientConfig();
  if (!cfg.apiBaseUrl) {
    return { success: false, message: 'API URL not configured.' };
  }
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (cfg.dbName) headers['x-client-db'] = cfg.dbName;
    if (cfg.clientCode) headers['x-client-code'] = cfg.clientCode;

    const response = await fetch(`${getBase()}auth/login`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        username: username.trim(),
        password,
        device_platform: 'web',
        device_name: 'Web Browser',
      }),
    });
    const data = await response.json();
    if (!data.success) {
      return { success: false, message: data.message || 'Login failed.' };
    }
    const { user, permissions, tokens, crm } = data.data;
    setStoredData(STORAGE_KEYS.USER_DATA, user);
    setStoredData(STORAGE_KEYS.TOKENS, tokens);
    setStoredData(STORAGE_KEYS.PERMISSIONS, permissions || []);
    setStoredData(STORAGE_KEYS.CRM_SETTINGS, crm || null);
    return { success: true, data: { user, permissions, tokens, crm } };
  } catch (err) {
    return { success: false, message: 'Connection failed.' };
  }
};

// ─── Forgot Password (public, no auth) ───────────────────
const publicAuthFetch = async (endpoint, body) => {
  const cfg = getClientConfig();
  if (!cfg.apiBaseUrl) {
    return { success: false, message: 'API URL not configured.' };
  }
  try {
    const headers = { 'Content-Type': 'application/json' };
    if (cfg.dbName) headers['x-client-db'] = cfg.dbName;
    if (cfg.clientCode) headers['x-client-code'] = cfg.clientCode;
    const response = await fetch(`${getBase()}auth/${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    const data = await response.json();
    return { success: !!data.success, message: data.message, data: data.data };
  } catch {
    return { success: false, message: 'Connection failed.' };
  }
};

export const forgotPasswordApi = (email) =>
  publicAuthFetch('forgot-password', { email });

export const verifyOtpApi = (email, otp) =>
  publicAuthFetch('verify-otp', { email, otp });

export const resetPasswordApi = (email, otp, new_password) =>
  publicAuthFetch('reset-password', { email, otp, new_password });

// ─── API Fetch (authenticated) ───────────────────────────
export const apiFetch = async (endpoint, options = {}) => {
  const cfg = getClientConfig();
  const tokens = getStoredData(STORAGE_KEYS.TOKENS);
  if (!cfg.apiBaseUrl) throw new Error('API URL not configured');

  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.slice(1) : endpoint;
  const url = `${getBase()}${cleanEndpoint}`;

  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers || {}),
  };

  if (tokens?.access_token) {
    headers['Authorization'] = `Bearer ${tokens.access_token}`;
  }
  if (cfg.dbName) {
    headers['x-client-db'] = cfg.dbName;
  }
  if (cfg.clientCode) {
    headers['x-client-code'] = cfg.clientCode;
  }

  const response = await fetch(url, { ...options, headers });
  const data = await response.json();

  // If 401, try refresh
  if (response.status === 401 && tokens?.refresh_token && !options._isRetry) {
    const refreshed = await refreshTokens();
    if (refreshed) {
      return apiFetch(endpoint, { ...options, _isRetry: true });
    } else {
      // Clear all and redirect to login
      Object.values(STORAGE_KEYS).forEach(k => removeStoredData(k));
      window.location.href = '/login';
    }
  }

  return { status: response.status, ...data };
};

// ─── Refresh Tokens ──────────────────────────────────────
const refreshTokens = async () => {
  try {
    const cfg = getClientConfig();
    const tokens = getStoredData(STORAGE_KEYS.TOKENS);
    if (!tokens?.refresh_token || !cfg.apiBaseUrl) return false;

    const headers = { 'Content-Type': 'application/json' };
    if (cfg.dbName) headers['x-client-db'] = cfg.dbName;
    if (cfg.clientCode) headers['x-client-code'] = cfg.clientCode;

    const response = await fetch(`${getBase()}auth/refresh`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ refresh_token: tokens.refresh_token }),
    });
    const data = await response.json();
    if (data.success && data.data?.tokens) {
      setStoredData(STORAGE_KEYS.TOKENS, data.data.tokens);
      return true;
    }
    return false;
  } catch {
    return false;
  }
};

// ─── Logout ──────────────────────────────────────────────
// Best-effort: tell backend to deactivate this device's FCM token + revoke
// the refresh token, then clear local storage. Network failure is non-fatal.
export const logoutApi = async () => {
  try {
    const fcmToken = localStorage.getItem(STORAGE_KEYS.FCM_TOKEN);
    const tokens = getStoredData(STORAGE_KEYS.TOKENS);
    if (tokens?.access_token) {
      await apiFetch('auth/logout', {
        method: 'POST',
        body: JSON.stringify({
          refresh_token: tokens.refresh_token || null,
          device_token: fcmToken || null,
        }),
      }).catch(() => {});
    }
  } catch {}
  Object.values(STORAGE_KEYS).forEach(k => removeStoredData(k));
  removeStoredData('rabs_impersonator_backup');
};

// ─── Helpers ─────────────────────────────────────────────
export const getStoredUser = () => getStoredData(STORAGE_KEYS.USER_DATA);
export const getStoredTokens = () => getStoredData(STORAGE_KEYS.TOKENS);
export const getStoredClient = () => getClientConfig();
export const getStoredPermissions = () => getStoredData(STORAGE_KEYS.PERMISSIONS);
